import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface BracketMatch {
  id: string;
  phase: string;
  team1: string;
  team2: string;
  score: string;
  winner?: number;
  scheduledDate?: string;
  court?: string;
}

export function useEliminationBracket(eventId: string, category?: string | null, subcategory?: string | null) {
  const [matches, setMatches] = useState<BracketMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
      async function fetchBracket() {
        try {
          setLoading(true);
          
          let query = supabase
            .from('tournament_match_results')
            .select('*')
            .eq('tournament_id', eventId)
            .in('phase', ['octavos', 'cuartos', 'semifinal', 'final']);
          
          if (category) {
            query = query.eq('category', category);
          }
          if (subcategory) {
            query = query.eq('subcategory', subcategory);
          }
          
          query = query.order('completed_at', { ascending: true });
          
          const { data, error: queryError } = await query;

        if (queryError) throw queryError;

        const bracketMatches: BracketMatch[] = data?.map(match => {
          const team1 = match.team1_player2 
            ? `${match.team1_player1} / ${match.team1_player2}`
            : match.team1_player1;
          
          const team2 = match.team2_player2
            ? `${match.team2_player1} / ${match.team2_player2}`
            : match.team2_player1;

          return {
            id: match.id,
            phase: match.phase,
            team1,
            team2,
            score: match.score,
            winner: match.winner_team,
            scheduledDate: match.scheduled_date,
            court: match.court_name
          };
        }) || [];

        setMatches(bracketMatches);
        setError(null);
      } catch (err) {
        console.error('[useEliminationBracket] Error:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar bracket');
      } finally {
        setLoading(false);
      }
    }

    fetchBracket();

    const channel = supabase
      .channel(`elimination-bracket-${eventId}-${category}-${subcategory}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tournament_match_results',
        filter: `tournament_id=eq.${eventId}`
      }, () => {
        fetchBracket();
      })
      .subscribe();

    const pollInterval = setInterval(() => {
      fetchBracket();
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [eventId, category, subcategory]);

  return { matches, loading, error };
}
